import { createSelector } from '@reduxjs/toolkit';
import lodashFilter from 'lodash/filter';
import lodashFind from 'lodash/find';
import lodashFlatMap from 'lodash/flatMap';
import lodashIsEmpty from 'lodash/isEmpty';

import CartHelper from '../../Helper/Cart';

// Do not remove import mealPlanDV its used on js doc
import { checkout, mealPlan as mealPlanDV } from '../defaultValues';

/**
 * @typedef {typeof mealPlanDV.hint} HintState
 */
const mealPlan = (state) => state.mealPlan.data;
/**
 * @param {RootState} state
 * @returns {HintState}
 */
const mealPlanHint = (state) => state.mealPlan.hint;
const ARRAY_PLACEHOLDER = [];
const selectIsCheckingOut = createSelector(
  mealPlan,
  (data) => data.isCheckingOut,
);
const selectIsDayHasCart = createSelector(
  [mealPlan, (_, days) => days],
  (data, days) => {
    const daysCart = lodashFilter(data.cart, (c) => days.includes(c.day));
    const daysCartItems = lodashFlatMap(daysCart, (c) => c.items);
    return !lodashIsEmpty(daysCartItems);
  },
);
const selectMealPlanCart = createSelector(mealPlan, (data) => data.cart);
const selectMealPlanCartByDay = createSelector(
  [selectMealPlanCart, (_, day) => day],
  (cart, day) => {
    const result = lodashFind(cart, { day });
    if (lodashIsEmpty(result)) return ARRAY_PLACEHOLDER;
    return lodashFind(cart, { day }).items;
  },
);
// for counter of monday and selected day cart items
const selectMealPlanCounter = createSelector(
  [(state) => selectMealPlanCartByDay(state, 1), selectMealPlanCartByDay],
  (mondayCart, selectedDayCart) => {
    const mondayCartQty = CartHelper.getTotalItemsCount(mondayCart);
    const selectedDayCartQty = CartHelper.getTotalItemsCount(selectedDayCart);
    return {
      monday: mondayCartQty,
      current: selectedDayCartQty,
    };
  },
);
// for checking if current selected day cart is exceeding monday cart
const selectMealPlanCartExceedMonday = createSelector(
  selectMealPlanCounter,
  (total) => {
    const isExceed = total.current > total.monday;
    return {
      isExceed: isExceed,
      exceeding: isExceed ? total.current - total.monday : 0,
    };
  },
);
const selectMealPlanCartFlat = createSelector(mealPlan, (data) =>
  lodashFlatMap(data.cart, (e) => e.items),
);
const selectHasMealPlanItem = createSelector(
  selectMealPlanCartFlat,
  (cart) => cart.length !== 0,
);
const selectMealPlanCheckoutData = createSelector(
  mealPlan,
  (state) => state.checkoutData,
);
const selectMealPlanCheckoutFlag = createSelector(
  mealPlan,
  (state) => state.checkoutFlag,
);
const selectMealPlanCheckoutPromo = createSelector(
  mealPlan,
  (state) => state.checkoutData[checkout.keys.PROMO_CODE],
);
const selectMealPlanDay = createSelector(mealPlan, (data) => data.day);
/**
 * @template {keyof HintState} K
 * @param {K} hintKey
 * @returns {(state: RootState) => HintState[K]}
 */
const selectMealPlanHint = (hintKey) =>
  createSelector(mealPlanHint, (hint) => hint[hintKey]);
const selectMealPlanStorePromo = createSelector(
  mealPlan,
  (state) => state.checkoutData[checkout.keys.PROMO_STORE],
);
const selectMealPlanType = createSelector(mealPlan, (data) => data.type);

export {
  selectIsDayHasCart,
  selectIsCheckingOut,
  selectMealPlanCart,
  selectMealPlanCartByDay,
  selectMealPlanCounter,
  selectMealPlanCartExceedMonday,
  selectMealPlanCartFlat,
  selectHasMealPlanItem,
  selectMealPlanCheckoutData,
  selectMealPlanCheckoutFlag,
  selectMealPlanCheckoutPromo,
  selectMealPlanDay,
  selectMealPlanHint,
  selectMealPlanStorePromo,
  selectMealPlanType,
};
